import React from 'react';
import { Card, Skeleton, SkeletonButton, SkeletonText } from '@parsina/framework';
import { C } from '../../constants/theme';
import Panel from '../../components/common/Panel.jsx';
import {
  SkeletonPage, InnerHeaderSkeleton, MobileHeaderSkeleton, MobileTilesSkeleton,
  SectionNavSkeleton, SegmentedSkeleton, OdontogramSkeleton, InfoRowsSkeleton,
  FieldSkeleton, TitledRowsSkeleton,
} from '../../components/common/skeletons.jsx';
import { MobileSectionTitle } from '../../components/mobile/MobileKit.jsx';

// پرونده — the loading shape of the patient workspace, desktop and mobile.
//
// Two layers, because the record loads in two steps:
//   1. the PAGE skeleton, while `usePatient` resolves — header band, tiles,
//      tab strip, and the body of whichever tab the URL names (a reload on
//      /patients/:id/chart draws the chart's shape, not خلاصه's);
//   2. the TAB skeletons, used on their own by the tabs that fetch for
//      themselves (چارت دندان reads `dentalChart` after the record is up), so
//      only that tab's body shimmers while the header and the other tabs stay
//      real (DESIGN.md §"Loading & skeleton", "Region load").
//
// Geometry: SummaryTab's field grid (minmax 180px, gap 18), HistoryTab's two
// panels, ChartTab's stat strip (84px tiles) + odontogram box + legend band;
// on mobile MobilePatientSummary's info-row card, MobilePatientChart's tiles +
// arch + picker, MobilePatientHistory's cards.

// ── desktop tabs ────────────────────────────────────────────────────────────

export const SummaryTabSkeleton = () => (
  <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <Panel title={<Skeleton width={120} height={14} />}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 18 }}>
        {Array.from({ length: 9 }, (_, i) => <FieldSkeleton key={i} valueWidth={`${50 + ((i * 17) % 40)}%`} />)}
      </div>
    </Panel>
    <Panel title={<Skeleton width={90} height={14} />}>
      <TitledRowsSkeleton count={2} gap={10} />
    </Panel>
  </div>
);

export const HistoryTabSkeleton = () => (
  <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <Panel title={<Skeleton width={110} height={14} />}>
      <TitledRowsSkeleton count={3} />
    </Panel>
    <Panel title={<Skeleton width={90} height={14} />}>
      <TitledRowsSkeleton count={3} />
    </Panel>
  </div>
);

/** ChartTab's stat tile: 84px min, 11px label over an 18px number. */
const ChartStatSkeleton = () => (
  <div
    style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '10px 14px', minWidth: 84, display: 'flex', flexDirection: 'column', gap: 6,
    }}
  >
    <Skeleton width={48} height={11} />
    <Skeleton width={28} height={18} />
  </div>
);

export const ChartTabSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="chart-skeleton">
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
      {Array.from({ length: 5 }, (_, i) => <ChartStatSkeleton key={i} />)}
      <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8 }}>
        <SkeletonButton width={132} />
        <SkeletonButton width={132} />
        <SkeletonButton width={96} />
      </div>
    </div>
    <OdontogramSkeleton />
    <div
      style={{
        display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12, padding: '10px 12px',
        background: C.bg, borderRadius: 10,
      }}
    >
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Skeleton width={12} height={12} radius={3} />
          <Skeleton width={[52, 72, 60, 84][i % 4]} height={11} />
        </div>
      ))}
    </div>
  </SkeletonPage>
);

/** The merged tooth trail inside چارت's drawer: a date column and a line. */
export const ToothHistorySkeleton = ({ label }) => (
  <SkeletonPage
    label={label}
    data-testid="chart-tooth-history-skeleton"
    style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
  >
    {[0, 1, 2].map((i) => (
      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Skeleton width={64} height={11} />
        <Skeleton width={`${40 + ((i * 17) % 30)}%`} height={12} />
      </div>
    ))}
  </SkeletonPage>
);

const TAB_BODY = {
  summary: SummaryTabSkeleton,
  history: HistoryTabSkeleton,
  chart: ChartTabSkeleton,
};

export const PatientDetailSkeletonWeb = ({ label, tab = 'summary' }) => {
  const Body = TAB_BODY[tab] || SummaryTabSkeleton;
  return (
    <SkeletonPage
      label={label}
      data-testid="patient-content"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}
    >
      <InnerHeaderSkeleton avatar meta stats={3} actions={1} />
      <div style={{ padding: '14px 28px 0', flexShrink: 0 }}>
        <SegmentedSkeleton count={3} />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '18px 28px 40px' }}>
        <Body />
      </div>
    </SkeletonPage>
  );
};

// ── mobile sections ─────────────────────────────────────────────────────────

export const MobileSummarySkeleton = () => (
  <div aria-hidden>
    <MobileSectionTitle><Skeleton width={96} height={12} /></MobileSectionTitle>
    <Card style={{ padding: '0.25rem 0.875rem' }}>
      <InfoRowsSkeleton count={7} />
    </Card>
    <MobileSectionTitle style={{ marginTop: '0.875rem' }}><Skeleton width={72} height={12} /></MobileSectionTitle>
    <Card style={{ padding: '0.875rem' }}>
      <SkeletonText lines={2} height={12} />
    </Card>
  </div>
);

export const MobileHistorySkeleton = () => (
  <div aria-hidden>
    {[0, 1].map((k) => (
      <div key={k} style={{ marginBottom: k === 0 ? '0.875rem' : 0 }}>
        <MobileSectionTitle><Skeleton width={90} height={12} /></MobileSectionTitle>
        <Card style={{ padding: '0.25rem 0.875rem' }}>
          <InfoRowsSkeleton count={3} />
        </Card>
      </div>
    ))}
  </div>
);

/** MobileToothPicker: four quadrants in a 2-up grid, each a wrap of 44px chips. */
const ToothPickerSkeleton = () => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
    {[0, 1, 2, 3].map((q) => (
      <div key={q} style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <Skeleton width={64} height={11} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(2.75rem, 1fr))', gap: '0.375rem' }}>
          {Array.from({ length: 8 }, (_, i) => <Skeleton key={i} width="100%" height={44} radius={10} />)}
        </div>
      </div>
    ))}
  </div>
);

export const MobileChartSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="chart-skeleton">
    <MobileTilesSkeleton count={4} />
    <MobileSectionTitle style={{ marginTop: '0.875rem' }} action={<SkeletonButton width={104} />}>
      <Skeleton width={110} height={12} />
    </MobileSectionTitle>
    <OdontogramSkeleton radius="0.75rem" />
    <div style={{ marginTop: '0.875rem' }}>
      <MobileSectionTitle><Skeleton width={96} height={12} /></MobileSectionTitle>
      <Skeleton width="80%" height={11} style={{ marginBottom: '0.625rem' }} />
      <ToothPickerSkeleton />
    </div>
  </SkeletonPage>
);

const MOBILE_SECTION = {
  summary: MobileSummarySkeleton,
  history: MobileHistorySkeleton,
  chart: MobileChartSkeleton,
};

export const PatientDetailSkeletonMobile = ({ label, tab = 'summary' }) => {
  const Body = MOBILE_SECTION[tab] || MobileSummarySkeleton;
  return (
    <SkeletonPage
      label={label}
      data-testid="patient-content"
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <MobileHeaderSkeleton back subtitle />
      <div style={{ padding: '0 0.75rem', marginTop: '-1rem', position: 'relative' }}>
        <MobileTilesSkeleton count={4} />
      </div>
      <div style={{ marginTop: '0.75rem' }}>
        <SectionNavSkeleton count={3} />
      </div>
      <div style={{ padding: '0.875rem 0.75rem 1.5rem' }}>
        <Body />
      </div>
    </SkeletonPage>
  );
};
